import { useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  ArrowUpRight,
  Play,
  Radio,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { CoachModal } from "@/features/coach/components/CoachModal";
import { hostKind } from "@/lib/hostKind";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import { FoundingClubForm } from "./FoundingClubForm";
import "../marketing-home.css";

const MATCH_IMAGE = "/hero-match.jpg";
const CAPTURE = {
  recording: {
    label: "Match recording",
    eyebrow: "From the match",
    title: "Turn a moment into a lesson.",
    text: "Start with a match recording. Pick the moment that matters, add your feedback and give the player something clear to work on.",
  },
  board: {
    label: "Tactical board",
    eyebrow: "From the training pitch",
    title: "A clear picture. Without a recording.",
    text: "Use the tactical board to explain positioning, movement or a team idea. A useful conversation doesn’t depend on a camera.",
  },
  live: {
    label: "Live stream",
    eyebrow: "For the whole team",
    title: "Bring the touchline closer.",
    text: "Give families a place to follow the match live, then revisit it when a recording is available.",
  },
} as const;
const PRIVACY = {
  player: {
    label: "Players",
    title: "Your feedback is for you.",
    text: "Players see feedback shared with them, their own goals and the team context their coaches choose to share.",
  },
  guardian: {
    label: "Parents & guardians",
    title: "Stay connected to their development.",
    text: "Guardians see the feedback and goals shared with their linked players. Team access follows the club’s permissions.",
  },
  coach: {
    label: "Coaches",
    title: "Space to coach thoughtfully.",
    text: "Coaches choose the audience for each note. A coach-only note remains separate from the feedback shared with players and families.",
  },
  club: {
    label: "Club administrators",
    title: "Run the club with clear boundaries.",
    text: "Club administration manages membership, consent and media operations. The club-admin role does not grant coaching-content access.",
  },
} as const;

/** Approved v5 marketing composition. Demonstrations are explicitly illustrative;
 * enquiry submission uses the existing persisted marketing lead API. */
export function LandingPage() {
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  useDocumentMeta({
    title: "tktic — coaching feedback, delivered",
    description:
      "Keep the coaching going beyond the touchline. Share useful feedback, bring matches to families and help players see their next step.",
    ogType: "website",
  });
  const showSampleFeedback = () => {
    const sample = document.getElementById("sample-feedback");
    sample?.focus({ preventScroll: true });
    sample?.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  return (
    <main className="marketing-home" data-testid="landing-page">
      <section className="home-hero" data-testid="landing-hero">
        <div className="home-hero-copy">
          <div className="home-eyebrow">For every player. At every level.</div>
          <h1 data-testid="landing-hero-headline">
            Make every moment <em>count.</em>
          </h1>
          <p data-testid="landing-hero-subhead">
            Keep the coaching going beyond the touchline. Share useful feedback,
            bring matches to families and help players see their next step.
          </p>
          <Button
            data-testid="landing-cta-hub"
            onClick={() => setEnquiryOpen(true)}
          >
            Meet Tktic <ArrowUpRight />
          </Button>
          <span className="home-small">
            For youth teams, coaches and the people behind them.
          </span>
        </div>
        <div className="home-hero-photo">
          <img src={MATCH_IMAGE} alt="Young football players on the pitch" />
          <div className="home-photo-note">
            <div className="home-avatar">AM</div>
            <div>
              <span className="home-small home-muted">
                Coach Alex · Sample feedback
              </span>
              <p>
                “That early look gave you more time. Bring it into your next
                game.”
              </p>
            </div>
          </div>
        </div>
      </section>
      <section className="home-section" id="how">
        <SectionHeading
          title="Good feedback belongs in every team."
          text="The small, personal moments matter. Tktic gives them a place to land, whatever level you play at."
        />
        <div className="home-grid home-three">
          {[
            [
              "01",
              "Notice the moment",
              "From a game, a training session or a tactical idea. Capture what will help a player.",
            ],
            [
              "02",
              "Make it personal",
              "Share a clear observation and a useful next step, with the right people.",
            ],
            [
              "03",
              "Keep the conversation going",
              "Players can reflect, respond and come back to their feedback through the season.",
            ],
          ].map(([n, title, text]) => (
            <div className="home-step" key={n}>
              <div className="home-number">{n}</div>
              <h3>{title}</h3>
              <p className="home-muted">{text}</p>
            </div>
          ))}
        </div>
      </section>
      <CaptureSection onDemo={showSampleFeedback} />
      <FeedbackDemonstration />
      <section className="home-section" id="teams">
        <SectionHeading
          title="One club. Different journeys."
          text="A shared home for team video and individual development, with the right view for each person."
        />
        <div className="home-grid home-three home-audience">
          {[
            [
              "Coaches",
              "Spend time coaching.",
              "Quick feedback and richer match review, ready for the moments you have.",
              "Explore coaching",
            ],
            [
              "Players & families",
              "See the next step.",
              "A place for personal feedback, goals and match moments throughout the season.",
              "Explore feedback",
            ],
            [
              "Clubs",
              "Bring teams together.",
              "Organize your squads, membership and match media in one club space.",
              "Explore club support",
            ],
          ].map(([role, title, text, action]) => (
            <div className="home-pane" key={role}>
              <div className="home-eyebrow">{role}</div>
              <h3>{title}</h3>
              <p className="home-muted">{text}</p>
              <Button
                variant="ghost"
                onClick={
                  role === "Clubs"
                    ? () => setEnquiryOpen(true)
                    : showSampleFeedback
                }
              >
                {action}
              </Button>
            </div>
          ))}
        </div>
      </section>
      <PrivacySection />
      <section className="home-cta" id="join" data-testid="landing-join">
        <div className="home-eyebrow">Let’s build better seasons</div>
        <h2>More support. More teams. More possibility.</h2>
        <p>
          Tell us about your club and how you’d like to keep players connected
          to their development.
        </p>
        <Button variant="secondary" onClick={() => setEnquiryOpen(true)}>
          Talk to us about your club
        </Button>
      </section>
      <footer className="home-footer" data-testid="landing-footer">
        <span data-testid="landing-footer-brand">© 2026 Tktic</span>
        <nav aria-label="Footer">
          {hostKind() === "club" && <Link to="/teams">Browse teams</Link>}
          <Link to="/privacy" data-testid="landing-footer-privacy">
            Privacy
          </Link>
          <Link to="/terms" data-testid="landing-footer-terms">
            Terms
          </Link>
        </nav>
      </footer>
      {enquiryOpen && (
        <CoachModal
          title="Let’s talk about your club"
          onClose={() => setEnquiryOpen(false)}
        >
          <FoundingClubForm />
        </CoachModal>
      )}
    </main>
  );
}

function SectionHeading({
  title,
  text,
  eyebrow,
}: {
  title: string;
  text: string;
  eyebrow?: string;
}) {
  return (
    <div className="home-section-heading">
      <div>
        {eyebrow && <div className="home-eyebrow">{eyebrow}</div>}
        <h2>{title}</h2>
      </div>
      <p className="home-muted">{text}</p>
    </div>
  );
}
function CaptureSection({ onDemo }: { onDemo: () => void }) {
  const [mode, setMode] = useState<keyof typeof CAPTURE>("recording");
  const copy = CAPTURE[mode];
  return (
    <section className="home-section" id="capture">
      <SectionHeading
        eyebrow="Coaching, in context"
        title="Different ways to make it click."
        text="Use the evidence you have. A recording when it’s available, a board when it isn’t."
      />
      <div className="home-tabs" aria-label="Ways to share">
        {(Object.keys(CAPTURE) as Array<keyof typeof CAPTURE>).map((key) => (
          <Button
            variant="ghost"
            key={key}
            aria-pressed={mode === key}
            onClick={() => setMode(key)}
          >
            {CAPTURE[key].label}
          </Button>
        ))}
      </div>
      <div className="home-demo home-pane home-tone">
        <div className="home-stack">
          <div className="home-eyebrow">{copy.eyebrow}</div>
          <h3>{copy.title}</h3>
          <p className="home-muted">{copy.text}</p>
          <div>
            <Button onClick={onDemo}>See it in action</Button>
          </div>
        </div>
        {mode === "board" ? (
          <div className="home-pitch">
            <img
              src="/landing/approved-pitch.svg"
              alt="Sample tactical football board"
            />
            <span className="home-avatar" style={{ left: "35%", top: "45%" }}>
              8
            </span>
            <span className="home-avatar" style={{ left: "65%", top: "22%" }}>
              11
            </span>
          </div>
        ) : (
          <div className="home-media">
            <img src={MATCH_IMAGE} alt="Illustrative youth football match" />
            <div className="home-media-caption">
              <span>
                {mode === "live"
                  ? "Live match experience"
                  : "Every moment can teach something."}
              </span>
              {mode === "live" ? <Radio /> : <Play />}
            </div>
            <span className="home-media-tag">Illustrative match image</span>
          </div>
        )}
      </div>
    </section>
  );
}
function FeedbackDemonstration() {
  const [sent, setSent] = useState(false);
  const [responded, setResponded] = useState(false);
  return (
    <section
      className="home-section home-grid home-two"
      aria-label="Sample feedback conversation"
      id="sample-feedback"
      tabIndex={-1}
    >
      <div className="home-pane">
        <div className="home-eyebrow">Coach sends</div>
        <h2>“Look before the ball arrives.”</h2>
        <p className="home-feedback-copy home-muted">
          Drew, you found space well in the second half. Next time, check your
          shoulder before receiving so your first touch can take you forward.
        </p>
        <Button
          variant={sent ? "outline" : "primary"}
          onClick={() => setSent(true)}
        >
          {sent ? "Feedback shared" : "Share this sample feedback"}
        </Button>
      </div>
      <div className="home-cyan">
        <div className="home-eyebrow">Player responds</div>
        <h2>
          {responded
            ? "“I’ll try this in training.”"
            : "A next step they understand."}
        </h2>
        <p className="home-feedback-copy">
          {sent
            ? "The sample feedback is ready for Drew to review."
            : "Share the sample feedback to see the player’s response."}
        </p>
        {sent ? (
          <Button variant="outline" onClick={() => setResponded(true)}>
            {responded ? "Response saved" : "Got it"}
          </Button>
        ) : (
          <span className="home-badge">Waiting for coach feedback</span>
        )}
      </div>
    </section>
  );
}
function PrivacySection() {
  const [role, setRole] = useState<keyof typeof PRIVACY>("player");
  return (
    <section
      className="home-section"
      id="privacy"
      data-testid="landing-privacy"
    >
      <SectionHeading
        title="A clear view of who sees what."
        text="Youth sport needs thoughtful boundaries. Access follows a person’s role and team."
      />
      <div className="home-privacy">
        <div className="home-privacy-people">
          {(Object.keys(PRIVACY) as Array<keyof typeof PRIVACY>).map((key) => (
            <Button
              variant="ghost"
              key={key}
              aria-pressed={key === role}
              onClick={() => setRole(key)}
            >
              {PRIVACY[key].label}
              <ArrowRight />
            </Button>
          ))}
        </div>
        <div className="home-pane home-tone home-stack">
          <div className="home-empty-icon">
            <ShieldCheck />
          </div>
          <h3>{PRIVACY[role].title}</h3>
          <p className="home-muted">{PRIVACY[role].text}</p>
          <Button variant="ghost" asChild>
            <Link to="/privacy">Read our privacy policy</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
